import { Button, Card, Space, Table, Tag, Typography } from 'antd';
import { PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { MockBanner } from '../../pages/components/MockBanner';
import { PageHeader } from '../../pages/components/PageHeader';
import { KpiCard } from '../../pages/components/KpiCard';

/** Org screen 8. Every figure is a computed read (D-02). */
export function OrgDashboardPage() {
  return (
    <div>
      <MockBanner when="the org-console read layer" needs="cross-institution query fanout" />
      <PageHeader
        title="Greenwood Group"
        subtitle="Oversee 2 schools and 1 college · every figure is a computed read, never a stored roll-up"
        actions={
          <>
            <Button icon={<SettingOutlined />} disabled>Trust settings</Button>
            <Button type="primary" icon={<PlusOutlined />} disabled>Add institution</Button>
          </>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, marginBottom: 12 }}>
        <KpiCard label="Students" value="6,240" foot="across 3 institutions" />
        <KpiCard label="Staff" value="284" foot="14 shared across institutions" />
        <KpiCard label="Collected MTD" value="₹4.86Cr" foot="of ₹6.20Cr · 78%" tone="ok" />
        <KpiCard label="Outstanding" value="₹1.34Cr" foot="612 students · 63 past 30d" tone="warn" />
      </div>

      <Card
        size="small"
        title="Institutions"
        style={{ marginBottom: 12 }}
        extra={<Typography.Text type="secondary" style={{ fontSize: 11.5 }}>Click a row to open its console</Typography.Text>}
      >
        <Table
          size="small"
          pagination={false}
          rowKey="slug"
          dataSource={INSTITUTIONS}
          columns={[
            { title: 'Institution', dataIndex: 'name', key: 'name', render: (v: string, r) => <div><div style={{ fontWeight: 600 }}>{v}</div><Typography.Text type="secondary" style={{ fontSize: 11.5, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{r.slug}</Typography.Text></div> },
            { title: 'Type', dataIndex: 'type', key: 'type', width: 90, render: (v: string) => <Tag color={v === 'school' ? 'gold' : 'blue'}>{v}</Tag> },
            { title: 'Students', dataIndex: 'students', key: 'students', width: 100, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</span> },
            { title: 'Term', dataIndex: 'term', key: 'term', width: 100 },
            { title: 'Attendance today', dataIndex: 'att', key: 'att', width: 130, render: (v: string) => <Tag color={parseFloat(v) < 92 ? 'warning' : 'success'}>{v}</Tag> },
            { title: 'Collections', dataIndex: 'collect', key: 'collect', width: 140, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</span> },
            { title: 'Issues', dataIndex: 'issues', key: 'issues', width: 100, render: (v: number) => v > 0 ? <Tag color="error">{v}</Tag> : <Tag color="success">Clear</Tag> },
          ]}
        />
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 12 }}>
        <Card size="small" title="Combined finance · this month">
          <div style={{ height: 12, background: '#eeeef1', borderRadius: 6, overflow: 'hidden', display: 'flex' }}>
            <div style={{ width: '52%', background: '#1c6442' }} title="Greenwood Senior" />
            <div style={{ width: '26%', background: '#2f6ab0' }} title="Greenwood Primary" />
            <div style={{ width: '22%', background: '#7d5610' }} title="Greenwood College" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginTop: 8, color: '#5b6478' }}>
            <span>Senior ₹2.53Cr</span>
            <span>Primary ₹1.26Cr</span>
            <span>College ₹1.07Cr</span>
          </div>
          <Typography.Text type="secondary" style={{ display: 'block', fontSize: 11.5, marginTop: 10 }}>
            The group total <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>equals</span> the sum of institutions — never a second stored copy (D-02).
          </Typography.Text>
        </Card>

        <Card size="small" title="Escalated to the trust">
          <ItemRow when="today" text="Capital spend · Senior · lab renovation ₹8.2L · above principal threshold" />
          <ItemRow when="2d" text="Fee concession · Primary · 12 hardship cases pending policy signoff" />
          <ItemRow when="1w" text="Staff transfer · A. Iyer · Senior → College · 01 Oct" last />
        </Card>
      </div>
    </div>
  );
}

function ItemRow({ when, text, last }: { when: string; text: string; last?: boolean }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr', gap: 10, padding: '8px 0', borderBottom: last ? 'none' : '1px solid #f1f1f4' }}>
      <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 11.5, color: '#5b6478' }}>{when}</div>
      <div style={{ fontSize: 12.5 }}>{text}</div>
    </div>
  );
}

const INSTITUTIONS = [
  { name: 'Greenwood Senior School', slug: 'gw-senior', type: 'school', students: '2,600', term: '2026–27 T2', att: '94.2%', collect: '₹2.53Cr / 78%', issues: 3 },
  { name: 'Greenwood Primary School', slug: 'gw-primary', type: 'school', students: '1,440', term: '2026–27 T2', att: '96.1%', collect: '₹1.26Cr / 82%', issues: 0 },
  { name: 'Greenwood College', slug: 'gw-college', type: 'college', students: '2,200', term: '2026–27 Sem 2', att: '89.4%', collect: '₹1.07Cr / 71%', issues: 2 },
];
